/** Movement module. Use MovementManager only. Public: update, jump, setMovementMode, getMovementMode, isGrounded, getVelocity, setPosition, getConfig. */

import { Object3D, Scene } from 'three';
import { Body } from 'cannon-es';
import { TransformMovementManager } from '../movement/TransformMovementManager';
import { PhysicsMovementManager } from '../movement/PhysicsMovementManager';
import { CollisionHandler } from '../collision/CollisionHandler';
import type { MovementConfig, MovementResult } from '../movement/Movement';
import type { ColliderConfig, CollisionResult, CollisionCheckOptions } from '../collision/CollisionDetection';

export const MovementType = {
    /** Kinematic movement (no physics engine) */
    Transform: 'transform',
    /** Physics-based movement (requires Cannon-es) */
    Physics: 'physics'
} as const;

export type MovementType = typeof MovementType[keyof typeof MovementType];

export const MovementMode = {
    /** Camera-relative movement (third-person style) */
    CameraRelative: 'camera-relative',
    /** Transform-relative movement (first-person style) */
    TransformRelative: 'transform-relative',
    /** World-space movement (top-down style) */
    WorldSpace: 'world-space'
} as const;

export type MovementMode = typeof MovementMode[keyof typeof MovementMode];

export interface MovementManagerConfig extends MovementConfig {
    /** Movement type: 'transform' (kinematic) or 'physics' (physics-based) */
    movementType: MovementType;

    /** Collider configuration (only for transform movement) */
    collider?: ColliderConfig;

    /** Collision check options (only for transform movement) */
    collisionOptions?: CollisionCheckOptions;

    /** Enable collision detection (only for transform movement, default: true) */
    enableCollision?: boolean;

    /** Movement smoothing factor (default: 0.15) */
    movementSmoothing?: number;

    /** Rotation smoothing factor (default: 0.15) */
    rotationSmoothing?: number;

    /** Default movement mode (default: 'camera-relative') */
    defaultMovementMode?: MovementMode;
}

/** Main facade: movement, collision, jumping. Use MovementType.Transform or .Physics. */
export class MovementManager {
    private config: Required<Omit<MovementManagerConfig, 'collider' | 'collisionOptions'>> & {
        collider?: ColliderConfig;
        collisionOptions?: CollisionCheckOptions;
    };
    private movementType: MovementType;

    // Movement managers (only one will be active)
    private transformMovement?: TransformMovementManager;
    private physicsMovement?: PhysicsMovementManager;

    // Collision handler (only for transform movement)
    private collisionHandler?: CollisionHandler;

    // References
    private target: Object3D;
    // @ts-expect-error - Scene kept for API compatibility, may be used in future
    private _scene?: Scene;
    private body?: Body;

    /**
     * Create a Movement Manager
     * 
     * @param target - Three.js Object3D to control (player, character, etc.)
     * @param scene - Three.js Scene (required for collision detection)
     * @param config - Movement configuration
     * @param body - Cannon-es Body (required for physics movement)
     */
    constructor(
        target: Object3D,
        scene?: Scene,
        config: MovementManagerConfig = { movementType: MovementType.Transform },
        body?: Body
    ) {
        this.target = target;
        this._scene = scene;
        this.body = body;
        this.movementType = config.movementType;

        // Fill in default config
        this.config = {
            movementType: config.movementType,
            speed: config.speed ?? 5.0,
            sprintMultiplier: config.sprintMultiplier ?? 2.0,
            jumpForce: config.jumpForce ?? 10.0,
            gravity: config.gravity ?? -20.0,
            normalizeDiagonal: config.normalizeDiagonal ?? true,
            collider: config.collider || undefined,
            collisionOptions: config.collisionOptions || undefined,
            enableCollision: config.enableCollision ?? true,
            movementSmoothing: config.movementSmoothing ?? 0.15,
            rotationSmoothing: config.rotationSmoothing ?? 0.15,
            defaultMovementMode: config.defaultMovementMode ?? MovementMode.CameraRelative
        };

        // Initialize movement system based on type
        if (this.movementType === MovementType.Transform) {
            this.initializeTransformMovement();
        } else if (this.movementType === MovementType.Physics) {
            this.initializePhysicsMovement();
        }
    }

    /**
     * Initialize transform-based movement (kinematic)
     */
    private initializeTransformMovement(): void {
        this.transformMovement = new TransformMovementManager(this.target, {
            speed: this.config.speed,
            sprintMultiplier: this.config.sprintMultiplier,
            jumpForce: this.config.jumpForce,
            gravity: this.config.gravity,
            normalizeDiagonal: this.config.normalizeDiagonal
        });

        // Initialize collision handler if enabled
        if (this.config.enableCollision) {
            this.collisionHandler = new CollisionHandler(
                this.target,
                this.config.collider,
                this.config.collisionOptions
            );
        }
    }

    /**
     * Initialize physics-based movement
     */
    private initializePhysicsMovement(): void {
        if (!this.body) {
            throw new Error('MovementManager: Physics movement requires a Cannon-es Body. Pass body as 4th parameter.');
        }

        this.physicsMovement = new PhysicsMovementManager(
            {
                speed: this.config.speed,
                sprintMultiplier: this.config.sprintMultiplier,
                jumpForce: this.config.jumpForce,
                normalizeDiagonal: this.config.normalizeDiagonal
            },
            this.body,
            this.target
        );
    }

    // ===== UPDATE METHOD =====

    /**
     * Update movement and collision (call every frame)
     * 
     * This is the main method - it handles everything automatically:
     * - Applies movement based on movement mode
     * - Applies gravity (transform movement only)
     * - Resolves collisions (transform movement only)
     * - Updates grounded state
     * 
     * @param inputVector - Input vector from InputManager {x: strafe, y: forward}
     * @param isSprinting - Whether sprint is active
     * @param deltaTime - Time since last frame
     * @param movementMode - Override default movement mode (optional)
     * @param cameraYaw - Camera yaw angle in radians (optional, depends on movement mode)
     * @returns Movement result or collision result
     */
    update(
        inputVector: { x: number; y: number },
        isSprinting: boolean,
        deltaTime: number,
        movementMode?: MovementMode,
        cameraYaw?: number | null
    ): MovementResult | CollisionResult | void {
        const mode = movementMode ?? this.config.defaultMovementMode;

        if (this.movementType === MovementType.Transform) {
            return this.updateTransformMovement(inputVector, isSprinting, deltaTime, mode, cameraYaw ?? null);
        } else if (this.movementType === MovementType.Physics) {
            return this.updatePhysicsMovement(inputVector, isSprinting, deltaTime, cameraYaw ?? null);
        }
    }

    /**
     * Update transform movement
     */
    private updateTransformMovement(
        inputVector: { x: number; y: number },
        isSprinting: boolean,
        deltaTime: number,
        mode: MovementMode,
        cameraYaw: number | null
    ): MovementResult | CollisionResult {
        if (!this.transformMovement) {
            throw new Error('MovementManager: Transform movement not initialized');
        }

        let result: MovementResult;

        // Apply movement based on mode
        switch (mode) {
            case MovementMode.CameraRelative:
                if (cameraYaw === null) {
                    throw new Error('MovementManager: camera-relative mode requires cameraYaw parameter');
                }
                result = this.transformMovement.applyCameraRelativeMovement(
                    inputVector,
                    cameraYaw,
                    isSprinting,
                    deltaTime,
                    this.config.movementSmoothing,
                    this.config.rotationSmoothing
                );
                break;

            case MovementMode.TransformRelative:
                result = this.transformMovement.applyTransformRelativeMovement(
                    inputVector,
                    isSprinting,
                    deltaTime,
                    this.config.movementSmoothing
                );
                break;

            case MovementMode.WorldSpace:
                result = this.transformMovement.applyWorldMovement(
                    inputVector,
                    isSprinting,
                    deltaTime,
                    this.config.movementSmoothing
                );
                break;
        }

        // Apply gravity
        this.transformMovement.applyGravity(deltaTime);

        // Resolve collisions
        if (this.collisionHandler) {
            const isGrounded = this.collisionHandler.checkAndResolve();
            this.transformMovement.setGrounded(isGrounded);
        }

        return result;
    }

    /**
     * Update physics movement
     * 
     * NOTE: deltaTime is not used for physics movement as the physics engine
     * handles time-stepping internally. We still accept it for API consistency.
     */
    private updatePhysicsMovement(
        inputVector: { x: number; y: number },
        isSprinting: boolean,
        _deltaTime: number,
        cameraYaw: number | null
    ): void {
        if (!this.physicsMovement) {
            throw new Error('MovementManager: Physics movement not initialized');
        }

        if (cameraYaw === null) {
            throw new Error('MovementManager: Physics movement requires cameraYaw parameter');
        }

        // Physics engine handles time-stepping internally, so no deltaTime needed
        this.physicsMovement.applyMovementWithMeshRotation(
            inputVector,
            cameraYaw,
            isSprinting,
            this.config.rotationSmoothing
        );
    }

    // ===== JUMPING =====

    /**
     * Make the character jump
     * Automatically checks grounded state for transform movement
     * @param customJumpForce - Optional custom jump force
     * @returns True if jump was executed
     */
    jump(customJumpForce?: number): boolean {
        if (this.transformMovement) {
            if (this.transformMovement.isGrounded()) {
                console.log('jump', customJumpForce);
                return this.transformMovement.jump();
            }
            return false;
        } else if (this.physicsMovement) {
            return this.physicsMovement.jump(customJumpForce);
        }
        return false;
    }

    /**
     * Check if character is grounded (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * For physics movement, use your physics engine's collision detection
     * 
     * @returns True if on ground
     * @throws Error if called with MovementType.Physics
     */
    isGrounded(): boolean {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.isGrounded() is only available for Transform movement.\n' +
                'For Physics movement, use your physics engine\'s collision detection instead.'
            );
        }
        if (this.transformMovement) {
            return this.transformMovement.isGrounded();
        }
        return false;
    }

    /**
     * Get current vertical velocity (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * Useful for displaying jump/fall state in UI
     * 
     * @returns Vertical velocity in units per second
     * @throws Error if called with MovementType.Physics
     */
    getVerticalVelocity(): number {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.getVerticalVelocity() is only available for Transform movement.\n' +
                'For Physics movement, access body.velocity.y directly.'
            );
        }
        if (this.transformMovement) {
            return this.transformMovement.getVerticalVelocity();
        }
        return 0;
    }

    /**
     * Reset vertical velocity to zero (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * Useful when respawning or teleporting
     * 
     * @throws Error if called with MovementType.Physics
     */
    resetVelocity(): void {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.resetVelocity() is only available for Transform movement.\n' +
                'For Physics movement, set body.velocity directly.'
            );
        }
        if (this.transformMovement) {
            this.transformMovement.resetVelocity();
        }
    }

    // ===== CONFIGURATION =====

    /**
     * Set movement speed
     * @param speed - Base movement speed
     */
    setSpeed(speed: number): void {
        this.config.speed = speed;
        if (this.transformMovement) {
            this.transformMovement.setSpeed(speed);
        } else if (this.physicsMovement) {
            this.physicsMovement.setSpeed(speed);
        }
    }

    /**
     * Get current movement speed
     * @returns Movement speed
     */
    getSpeed(): number {
        if (this.transformMovement) {
            return this.transformMovement.getSpeed();
        } else if (this.physicsMovement) {
            return this.physicsMovement.getSpeed();
        }
        return this.config.speed;
    }

    /**
     * Set sprint multiplier
     * @param multiplier - Sprint speed multiplier
     */
    setSprintMultiplier(multiplier: number): void {
        this.config.sprintMultiplier = multiplier;
        if (this.transformMovement) {
            this.transformMovement.setSprintMultiplier(multiplier);
        } else if (this.physicsMovement) {
            this.physicsMovement.setSprintMultiplier(multiplier);
        }
    }

    /**
     * Get current sprint multiplier
     * @returns Sprint multiplier
     */
    getSprintMultiplier(): number {
        if (this.transformMovement) {
            return this.transformMovement.getSprintMultiplier();
        } else if (this.physicsMovement) {
            return this.physicsMovement.getSprintMultiplier();
        }
        return this.config.sprintMultiplier;
    }

    /**
     * Set default movement mode
     * @param mode - Movement mode
     */
    setDefaultMovementMode(mode: MovementMode): void {
        this.config.defaultMovementMode = mode;
    }

    /**
     * Get default movement mode
     * @returns Movement mode
     */
    getDefaultMovementMode(): MovementMode {
        return this.config.defaultMovementMode;
    }

    /**
     * Set movement smoothing factor
     * @param factor - Smoothing factor (0.0 = instant, 1.0 = very smooth)
     */
    setMovementSmoothing(factor: number): void {
        this.config.movementSmoothing = factor;
    }

    /**
     * Set rotation smoothing factor
     * @param factor - Smoothing factor (0.0 = instant, 1.0 = very smooth)
     */
    setRotationSmoothing(factor: number): void {
        this.config.rotationSmoothing = factor;
    }

    // ===== COLLISION CONFIGURATION (Transform Movement Only) =====

    /**
     * Add collision objects to check against (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * For physics movement, use your physics engine's collision system
     * 
     * @param objects - Single object or array of objects to collide with
     * @throws Error if called with MovementType.Physics
     */
    addCollisionObjects(objects: Object3D | Object3D[]): void {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.addCollisionObjects() is only available for Transform movement.\n' +
                'For Physics movement, configure collision bodies in your physics engine.'
            );
        }
        if (this.collisionHandler) {
            this.collisionHandler.addCollisionObjects(objects);
        }
    }

    /**
     * Clear all collision objects (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * 
     * @throws Error if called with MovementType.Physics
     */
    clearCollisionObjects(): void {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.clearCollisionObjects() is only available for Transform movement.\n' +
                'For Physics movement, manage collision bodies in your physics engine.'
            );
        }
        if (this.collisionHandler) {
            this.collisionHandler.clearCollisionObjects();
        }
    }

    /**
     * Enable collision detection (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * 
     * @param colliderConfig - Optional collider configuration
     * @param collisionOptions - Optional collision options
     * @throws Error if called with MovementType.Physics
     */
    enableCollision(colliderConfig?: ColliderConfig, collisionOptions?: CollisionCheckOptions): void {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.enableCollision() is only available for Transform movement.\n' +
                'For Physics movement, collision is handled by the physics engine.'
            );
        }
        if (this.collisionHandler) {
            this.collisionHandler.enable(colliderConfig, collisionOptions);
        }
    }

    /**
     * Disable collision detection (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * 
     * @throws Error if called with MovementType.Physics
     */
    disableCollision(): void {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.disableCollision() is only available for Transform movement.\n' +
                'For Physics movement, collision is handled by the physics engine.'
            );
        }
        if (this.collisionHandler) {
            this.collisionHandler.disable();
        }
    }

    /**
     * Update collision options (Transform Movement Only)
     * 
     * ⚠️ Only available when movementType is MovementType.Transform
     * 
     * @param options - Collision check options
     * @throws Error if called with MovementType.Physics
     */
    updateCollisionOptions(options: CollisionCheckOptions): void {
        if (this.movementType === MovementType.Physics) {
            throw new Error(
                'MovementManager.updateCollisionOptions() is only available for Transform movement.\n' +
                'For Physics movement, configure collision in your physics engine.'
            );
        }
        if (this.collisionHandler) {
            this.collisionHandler.updateOptions(options);
        }
    }

    // ===== UTILITY =====

    /**
     * Get the movement type
     * @returns Movement type ('transform' or 'physics')
     */
    getMovementType(): MovementType {
        return this.movementType;
    }

    /**
     * Get the target object being controlled
     * @returns Three.js Object3D
     */
    getTarget(): Object3D {
        return this.target;
    }

    /**
     * Get the physics body (physics movement only)
     * @returns Cannon-es Body or undefined
     */
    getBody(): Body | undefined {
        return this.body;
    }
}

